import {
  type Permission,
  type Profile,
  Role,
} from '@gm-commercial/profile-model';
import type { UserGrantsResponse } from '@gm-commercial/rtk-clients/permissions/types';
import type { ProfileResponse } from '@gm-commercial/rtk-clients/users/types';

import {
  type ApiAddressFields,
  mapApiResponseToAddress,
} from '~/common/mappers/common.mappers';
import type {
  ActivationStatus,
  PartialEntity,
} from '~/common/models/common.model';
import { BDAppErrorType, BDError } from '~/common/models/error.model';
import type { Fleet } from '~/common/models/fleet.model';
import type { Hub } from '~/common/models/hub.model';
import { OnboardingState } from '~/common/models/onboarding.model';
import type { Organization } from '~/common/models/organization.model';
import { lowercaseLiteral } from '~/common/utils/common.utils';
import { mapApiResponseInviteToUserInvite } from '~/features/users/list/mappers/users.mappers';

export interface Result {
  grantedPermissions: GrantedPermission[];
}
export interface GrantedPermission {
  scope: string;
  grants: { [key: string]: boolean };
}
export interface PermissionsContextIDS {
  fleetsId: string | null;
  hubsId: string | null;
  organizationsId: string | null;
}
export interface Permissions {
  permissions: Permission[];
  permissionsContextIds: PermissionsContextIDS;
}

export type ProfileResult = Omit<Profile, 'name' | 'address' | 'status'> & {
  displayName: string | null;
  address: ApiAddressFields;
  status?: Uppercase<ActivationStatus> | null;
  organization?: PartialEntity<Organization> | null;
  hub?: PartialEntity<Hub> | null;
  fleet?: PartialEntity<Fleet> | null;
} & ProfileResponse;

const getName = (result: ProfileResult) => {
  let name = [result.firstName, result.lastName].filter(Boolean).join(' ');
  if (result.displayName && result.displayName !== 'null null') {
    name = result.displayName;
  }
  return name;
};

export const mapApiResponseToProfile = (rawResult: ProfileResult): Profile => {
  if (rawResult.status) {
    const mapped: Profile = {
      ...rawResult,
      address: rawResult.address
        ? mapApiResponseToAddress(rawResult.address)
        : undefined,
      name: getName(rawResult),
      role: Role[rawResult.role as unknown as keyof typeof Role],
      status: lowercaseLiteral(rawResult.status) as ActivationStatus,
      organization: rawResult?.organization,
      hub: rawResult?.hub,
      fleet: rawResult?.fleet,
      invite: mapApiResponseInviteToUserInvite(rawResult.invite),
    };
    return mapped;
  }

  throw new BDError('Unexpected profile response', {
    type: BDAppErrorType.VALIDATION,
    data: rawResult,
  });
};

export const mapApiResponseToOnboardingState = (result?: OnboardingState) => {
  return result &&
    Object.values(OnboardingState).includes(result as OnboardingState)
    ? (result as OnboardingState)
    : OnboardingState.UNKNOWN;
};

export const mapOnboardingStateToApiResponse = (
  onBoardingState: OnboardingState
) => {
  return { onBoardingState };
};

export const mapPermissionResponse = (
  apiPermissionContext: UserGrantsResponse
): Permissions => {
  const consolidatedGrants: Record<string, boolean> = {};
  const permissions = apiPermissionContext?.grantedPermissions ?? [];
  for (const permission of permissions) {
    const grants = permission?.grants ?? {};
    for (const [action, isGranted] of Object.entries(grants)) {
      consolidatedGrants[action] = consolidatedGrants[action] || isGranted;
    }
  }
  const permissionsContextIds: PermissionsContextIDS = {
    organizationsId: null,
    fleetsId: null,
    hubsId: null,
  };
  const scope = apiPermissionContext?.grantedPermissions?.[0]?.scope;
  const arrayOfIds = scope?.split('/').filter(Boolean);

  //arrayOfIds may be empty, if grantedPermissions is an empty array (such as in the case of invited pending Users)
  //Hence why an early termination is required to not error out the method, which would lead to service unavailable in InboundRedirect.tsx
  //Only check if array of ids is falsy, since checking the length could result in false positive if length is 0.
  if (!arrayOfIds) {
    return {
      permissions: [],
      permissionsContextIds,
    };
  }

  for (let i = 0; i < arrayOfIds.length; i += 2) {
    const key = arrayOfIds[i];
    const value = arrayOfIds[i + 1];
    if (key === 'organization') {
      permissionsContextIds.organizationsId = value;
    } else if (key === 'fleet') {
      permissionsContextIds.fleetsId = value;
    } else if (key === 'hub') {
      permissionsContextIds.hubsId = value;
    }
  }
  return {
    permissions: Object.keys(consolidatedGrants).filter(
      (action) => consolidatedGrants[action]
    ) as Permission[],
    permissionsContextIds,
  };
};
